import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Button,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Checkbox,
} from "@mui/material";
import {
  Search,
  Refresh,
  PersonOutline,
  BusinessOutlined,
  DescriptionOutlined,
  FilterList,
  LocationOn,
  Close,
  CalendarToday,
  VerifiedUser,
  InsertDriveFileOutlined,
  Visibility,
  Download,
  CheckCircle,
} from "@mui/icons-material";
import AdminVerificationsSkeleton from "./AdminVerificationsSkeleton";
import {
  useGetUsersForVerificationQuery,
  useGetVerificationDetailQuery,
  useVerifyRegaAssetMutation,
  useVerifyNafathUserMutation,
  useVerifyTaufeeqMutation,
  useVerifyVaraDocumentMutation,
} from "../../../redux/api/authApi";

const TYPE_CHIP_KEY = {
  NAFATH: "infoDeep",
  REGA: "success",
  VARA: "violet",
  WATHQ: "successDeep",
  MISA: "pending",
  TAUFEEQ: "teal",
};

/** Main view: drives `role` + optional `tab` query params */
const VIEW = {
  INVESTOR: "investor",
  ASSET_OWNER: "assetOwner",
  TAUFEEQ: "taufeeq",
};

const ALL_TYPES = "all";
const ALL_STATUS = "all";

function getVerificationList(response) {
  const d = response?.data;
  if (Array.isArray(d)) return d;
  if (d && typeof d === "object") {
    const inner = d.users || d.items || d.results || d.docs || d.records;
    if (Array.isArray(inner)) return inner;
    if (Array.isArray(d.data)) return d.data;
  }
  return [];
}

function getVerificationPagination(response) {
  const p = response?.pagination;
  if (p && typeof p === "object") {
    return {
      total: Number(p.total ?? 0) || 0,
      page: Number(p.page ?? 1) || 1,
      limit: Number(p.limit ?? 10) || 10,
    };
  }
  const d = response?.data;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    return {
      total: Number(d.total ?? d.count ?? 0) || 0,
      page: Number(d.page ?? 1) || 1,
      limit: Number(d.limit ?? 10) || 10,
    };
  }
  return { total: 0, page: 1, limit: 10 };
}

/** API often sends `location` / `address` as an object, not a string */
function formatLocationField(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object" && !Array.isArray(value)) {
    const o = value;
    const parts = [
      o.streetAddress ?? o.addressLine1 ?? o.line1 ?? o.street,
      o.city ?? o.district,
      o.state ?? o.region,
      o.zipCode ?? o.postalCode ?? o.zip,
      o.country,
    ].filter((p) => p != null && String(p).trim() !== "");
    return parts.join(", ");
  }
  return String(value);
}

/**
 * `type` query param for GET /api/admin/verification/detail
 */
function verificationTypeQueryParam(typeLabel) {
  const u = String(typeLabel || "")
    .toUpperCase()
    .replace(/\s+/g, "");
  if (u === "VARA") return "VARA";
  if (u === "NAFATH") return "NAFATH";
  if (u === "REGA") return "REGA";
  if (u === "TAUFEEQ") return "TAUFEEQ";
  return u || "NAFATH";
}

function formatSubmittedAt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "long",
      timeStyle: "short",
    });
  } catch {
    return String(iso);
  }
}

function formatDetailValue(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try {
      return new Date(v).toLocaleString();
    } catch {
      return v;
    }
  }
  return String(v);
}

/** Omit UserID, RegaID, AssetID from "Additional details" (covered under User info / not needed here). */
function shouldShowInAdditionalDetails(key) {
  const n = String(key).replace(/[\s_-]/g, "").toLowerCase();
  return !["userid", "regaid", "assetid", "location"].includes(n);
}

function isLikelyMongoId(v) {
  const s = String(v ?? "").trim();
  return /^[a-f\d]{24}$/i.test(s);
}

/** Find a 24-char id on `obj` whose key looks like a user id field. */
function findMongoUserIdByKeyScan(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return "";
  for (const [k, v] of Object.entries(obj)) {
    if (v == null || typeof v === "object") continue;
    const s = String(v).trim();
    if (!isLikelyMongoId(s)) continue;
    const nk = k.replace(/[\s_-]/g, "").toLowerCase();
    if (
      nk === "userid" ||
      nk === "ownerid" ||
      nk === "owneruserid" ||
      nk === "assetownerid" ||
      nk === "createdby" ||
      nk === "applicantid" ||
      nk === "investorid" ||
      nk === "investoruserid" ||
      nk === "mongouserid" ||
      (nk.includes("user") && nk.includes("id"))
    ) {
      return s;
    }
  }
  return "";
}

const NEST_USER_KEYS = new Set([
  "user",
  "owner",
  "investor",
  "applicant",
  "submittedby",
  "createdby",
]);

/**
 * Case-insensitive key match + shallow recurse into user/owner/investor objects.
 * Picks up API shapes like `User ID`, `investorId`, nested `user._id`.
 */
function findUserIdDeep(obj, depth = 0) {
  if (!obj || typeof obj !== "object" || depth > 6) return "";
  if (Array.isArray(obj)) {
    for (const el of obj) {
      const x = findUserIdDeep(el, depth + 1);
      if (x) return x;
    }
    return "";
  }
  for (const [k, v] of Object.entries(obj)) {
    const nk = String(k).replace(/[\s_-]/g, "").toLowerCase();
    if (v != null && typeof v !== "object") {
      const s = String(v).trim();
      if (!s) continue;
      if (
        nk === "userid" ||
        nk === "user_id" ||
        nk === "mongodbuserid" ||
        nk === "investorid" ||
        nk === "investoruserid" ||
        nk === "applicantid" ||
        nk === "assetownerid" ||
        nk === "owneruserid"
      ) {
        return s;
      }
      if ((nk === "ownerid" || nk === "createdby") && isLikelyMongoId(s)) return s;
    }
    if (v != null && typeof v === "object" && !Array.isArray(v)) {
      if (NEST_USER_KEYS.has(nk)) {
        const id = v._id ?? v.id ?? v.userId ?? v.user_id;
        if (id != null && String(id).trim() !== "") return String(id).trim();
        const sub = findUserIdDeep(v, depth + 1);
        if (sub) return sub;
      } else {
        const skipNested = new Set([
          "location",
          "address",
          "documents",
          "files",
          "metadata",
        ]);
        if (!skipNested.has(nk) && depth < 5) {
          const sub = findUserIdDeep(v, depth + 1);
          if (sub) return sub;
        }
      }
    }
  }
  return "";
}

function resolveMongoUserId(detail, specific, user) {
  const nestedUser =
    specific?.user && typeof specific.user === "object" ? specific.user : {};
  const owner =
    specific?.owner != null && typeof specific.owner === "object"
      ? specific.owner
      : null;
  const investor =
    specific?.investor != null && typeof specific.investor === "object"
      ? specific.investor
      : null;
  const candidates = [
    specific?.userId,
    specific?.user_id,
    specific?.UserId,
    specific?.mongoUserId,
    specific?.applicantId,
    specific?.assetOwnerId,
    specific?.ownerUserId,
    specific?.ownerId,
    specific?.investorId,
    specific?.investorUserId,
    typeof specific?.owner === "string" ? specific.owner : null,
    owner?._id,
    owner?.id,
    owner?.userId,
    investor?._id,
    investor?.id,
    investor?.userId,
    nestedUser?._id,
    nestedUser?.id,
    user?._id,
    user?.id,
    user?.userId,
    user?.mongoUserId,
    detail?.userId,
  ];
  const found = candidates.find((x) => x != null && String(x).trim() !== "");
  if (found != null) return String(found).trim();

  const deep =
    findUserIdDeep(specific) ||
    findUserIdDeep(user) ||
    findUserIdDeep(detail);

  const scanned =
    findMongoUserIdByKeyScan(specific) ||
    findMongoUserIdByKeyScan(user) ||
    findMongoUserIdByKeyScan(detail);
  return deep || scanned || "";
}

/**
 * Match verification kind when API uses `type` and/or `verificationType`.
 * REGA is checked before VARA so combined strings like "VARA … REGA …" still resolve to REGA when appropriate.
 */
function resolveDetailVerificationKind(detail) {
  const combined = `${detail?.type ?? ""} ${detail?.verificationType ?? ""}`;
  const compact = combined.toUpperCase().replace(/\s+/g, "");
  if (compact.includes("TAUFEEQ")) return "TAUFEEQ";
  if (compact.includes("NAFATH")) return "NAFATH";
  if (compact.includes("REGA")) return "REGA";
  if (compact.includes("VARA") || compact.includes("VERA")) return "VARA";
  return String(detail?.type || "")
    .toUpperCase()
    .replace(/\s+/g, "");
}

function pickRegaRecordId(specific, detail, detailQueryId) {
  const candidates = [
    specific?.regaId,
    specific?.rega_id,
    specific?.RegaId,
    specific?.verificationId,
    specific?._id,
    detail?.regaId,
    detailQueryId,
  ];
  const found = candidates.find((x) => x != null && String(x).trim() !== "");
  return found != null ? String(found).trim() : "";
}

function pickVaraRecordId(specific, detail, detailQueryId) {
  const candidates = [
    specific?.varaId,
    specific?.vara_id,
    specific?.complianceId,
    specific?.varaDocumentId,
    specific?.verificationId,
    specific?._id,
    specific?.id,
    detail?.varaId,
    detailQueryId,
  ];
  const found = candidates.find((x) => x != null && String(x).trim() !== "");
  return found != null ? String(found) : "";
}

/**
 * Builds POST body for approve/reject. REGA: status boolean; NAFATH/TAUFEEQ/VARA: string statuses.
 * @param {"approve"|"reject"} decision
 * @param {string} [detailQueryId] — id used for GET /verification/detail (same as list `recordId`); required fallback for VARA when `specific.varaId` is absent.
 * @param {string} [listUserIdFallback] — Mongo user id from verification list row when detail payload omits it.
 * @param {boolean} [assetOwnersTab] — When true (Asset Owners tab), REGA/VARA verify bodies use list `recordId` (`detailQueryId`), not human-readable `id` from detail.
 * @param {string[]} [filesNeedToBeUpdated] — Display/file names for documents flagged on reject (empty on approve).
 */
function buildVerificationDecisionBody(
  detail,
  decision,
  rejectionReason,
  detailQueryId,
  listUserIdFallback,
  assetOwnersTab,
  filesNeedToBeUpdated = []
) {
  if (!detail || typeof detail !== "object") return null;
  const specific = detail.specific && typeof detail.specific === "object" ? detail.specific : {};
  const user = detail.user && typeof detail.user === "object" ? detail.user : {};
  const kind = resolveDetailVerificationKind(detail);
  const approve = decision === "approve";
  const filesList =
    approve || !Array.isArray(filesNeedToBeUpdated)
      ? []
      : filesNeedToBeUpdated.map((s) => String(s || "").trim()).filter(Boolean);
  const common = {
    rejectionReason: rejectionReason ?? "",
    filesNeedToBeUpdated: filesList,
  };

  const recordIdStr =
    detailQueryId != null && String(detailQueryId).trim() !== ""
      ? String(detailQueryId).trim()
      : "";

  const userId =
    resolveMongoUserId(detail, specific, user) || String(listUserIdFallback || "").trim();

  const recordIdForPayload = assetOwnersTab && recordIdStr ? recordIdStr : "";

  if (kind === "REGA") {
    const regaId = recordIdForPayload || pickRegaRecordId(specific, detail, detailQueryId);
    return {
      endpoint: "rega",
      body: {
        regaId,
        status: approve,
        ...common,
      },
      valid: Boolean(regaId),
    };
  }
  if (kind === "NAFATH") {
    return {
      endpoint: "nafath",
      body: {
        userId,
        nafathStatus: approve ? "Approved" : "Rejected",
        ...common,
      },
      valid: Boolean(userId),
    };
  }
  if (kind === "TAUFEEQ") {
    const assetId =
      specific.assetId != null && String(specific.assetId).trim() !== ""
        ? String(specific.assetId)
        : "";
    return {
      endpoint: "taufeeq",
      body: {
        assetId,
        isValuationReportVerified: approve ? "Approved" : "Rejected",
        ...common,
      },
      valid: Boolean(assetId),
    };
  }
  if (kind === "VARA") {
    const varaId = recordIdForPayload || pickVaraRecordId(specific, detail, detailQueryId);
    return {
      endpoint: "vara",
      body: {
        varaId,
        status: approve ? "Approved" : "Rejected",
        ...common,
      },
      // Enable actions when we have a vara record id (from specific or the same id used to load this detail).
      valid: Boolean(varaId),
    };
  }
  return null;
}

/** Map API row → table model (flexible field names) */
function mapVerificationRow(raw, view) {
  const r = raw || {};
  const user = r.user || r.userId || {};
  const displayId =
    r.verificationId ||
    r.requestId ||
    r.referenceId ||
    r.id ||
    r._id ||
    "—";
  const typeRaw =
    (r.verificationType || r.type || r.documentType || "NAFATH").toString();
  let typeUpper = typeRaw.toUpperCase();
  if (view === VIEW.TAUFEEQ) typeUpper = "TAUFEEQ";

  const name =
    r.name ||
    r.fullName ||
    user.name ||
    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
    r.companyName ||
    "—";
  const userSubId =
    r.userCode ||
    user.userCode ||
    user.reference ||
    (user._id ? `USR-${String(user._id).slice(-6)}` : null) ||
    r.email ||
    "";

  const location =
    formatLocationField(r.location) ||
    formatLocationField(r.address) ||
    formatLocationField(r.propertyAddress) ||
    [r.city, r.district, r.country].filter(Boolean).join(", ") ||
    "—";

  const statusRaw = (r.status || r.verificationStatus || "Pending").toString();
  const statusNorm = statusRaw.toLowerCase();

  const avatar = user.avatar || user.profileImage || r.avatar;

  /** Detail API expects DB id (`recordId`), not display `id` (e.g. TF-001). */
  const detailIdRaw =
    r.recordId ??
    r._id ??
    r.verificationId ??
    r.assetId ??
    r.requestId ??
    r.id;
  const detailId =
    detailIdRaw != null && detailIdRaw !== "" ? String(detailIdRaw) : "";

  const listUserIdRaw =
    user._id ??
    user.id ??
    user.userId ??
    r.userId ??
    r.ownerId ??
    r.ownerUserId ??
    r.applicantId ??
    r.mongoUserId ??
    r.investorId ??
    r.investorUserId;
  const listUserId =
    listUserIdRaw != null && String(listUserIdRaw).trim() !== ""
      ? String(listUserIdRaw).trim()
      : findUserIdDeep(r) || findMongoUserIdByKeyScan(r);

  return {
    key: String(r._id || r.id || displayId),
    displayId,
    typeLabel: typeUpper,
    name,
    userSubId,
    location,
    statusRaw,
    statusNorm,
    avatar,
    detailId,
    listUserId,
    raw: r,
  };
}

function TypeBadge({ label }) {
  const L = String(label || "NAFATH").toUpperCase();
  const key = TYPE_CHIP_KEY[L] || "slate";
  return (
    <Chip
      label={L}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: 11,
        height: 24,
        borderRadius: 1,
        bgcolor: (theme) => theme.palette.chips[key].bg,
        color: (theme) => theme.palette.chips[key].text,
      }}
    />
  );
}

function StatusCell({ statusNorm, statusRaw }) {
  const s = statusNorm;
  if (s.includes("approved") || s.includes("verified")) {
    return (
      <Typography
        fontSize={13}
        fontWeight={600}
        sx={{ color: (theme) => theme.palette.chips.success.text }}
      >
        {statusRaw}
      </Typography>
    );
  }
  if (s.includes("reject")) {
    return (
      <Chip
        label={statusRaw}
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: 11,
          height: 24,
          borderRadius: 1,
          bgcolor: (theme) => theme.palette.chips.error.bg,
          color: (theme) => theme.palette.chips.error.text,
        }}
      />
    );
  }
  if (s.includes("review") || s.includes("progress")) {
    return (
      <Chip
        label={statusRaw}
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: 11,
          height: 24,
          borderRadius: 1,
          bgcolor: (theme) => theme.palette.chips.infoDeep.bg,
          color: (theme) => theme.palette.chips.infoDeep.text,
        }}
      />
    );
  }
  if (s.includes("pending")) {
    return (
      <Typography fontSize={13} sx={{ color: "text.primary" }}>
        {statusRaw}
      </Typography>
    );
  }
  return (
    <Typography fontSize={13} sx={{ color: "text.muted" }}>
      {statusRaw}
    </Typography>
  );
}

const TERMINAL_VERIFICATION_STATUSES = new Set([
  "approved",
  "verified",
  "complete",
  "rejected",
  "declined",
]);

function isVerificationStatusTerminal(value) {
  const s = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!s) return false;
  return TERMINAL_VERIFICATION_STATUSES.has(s);
}

/** Collect string status fields the detail API may use (root or nested). */
function collectDetailStatusCandidates(detail) {
  if (!detail || typeof detail !== "object") return [];
  const strings = [];
  const push = (v) => {
    if (v == null || typeof v === "object") return;
    const t = String(v).trim();
    if (t) strings.push(t);
  };
  push(detail.status);
  push(detail.verificationStatus);
  push(detail.state);
  push(detail.nafathStatus);
  push(detail.varaStatus);
  const spec = detail.specific && typeof detail.specific === "object" ? detail.specific : null;
  if (spec) {
    push(spec.status);
    push(spec.nafathStatus);
    push(spec.varaStatus);
    push(spec.verificationStatus);
  }
  return strings;
}

function detailIndicatesTerminal(detail) {
  return collectDetailStatusCandidates(detail).some((s) => isVerificationStatusTerminal(s));
}

export function VerificationReviewDialog({
  open,
  onClose,
  loading,
  fetchError,
  detail,
  detailQueryId,
  listUserId,
  assetOwnersTab,
  onActionSuccess,
  verificationType = "",
  externalStatusHints = null,
}) {
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionReasonError, setRejectionReasonError] = useState("");
  const [actionError, setActionError] = useState(null);
  const [actionPending, setActionPending] = useState(false);
  /** After approve/reject succeeds, keep dialog open with actions disabled until user closes. */
  const [decisionSubmitted, setDecisionSubmitted] = useState(false);
  const [optimisticStatusLabel, setOptimisticStatusLabel] = useState(null);
  /** Indices into `documents` selected for reject → sent as `filesNeedToBeUpdated` (file display names). */
  const [selectedDocIndices, setSelectedDocIndices] = useState([]);

  const [verifyRega, { isLoading: regaBusy }] = useVerifyRegaAssetMutation();
  const [verifyNafath, { isLoading: nafathBusy }] = useVerifyNafathUserMutation();
  const [verifyTaufeeq, { isLoading: taufeeqBusy }] = useVerifyTaufeeqMutation();
  const [verifyVara, { isLoading: varaBusy }] = useVerifyVaraDocumentMutation();

  useEffect(() => {
    if (!open) {
      setRejectionReason("");
      setRejectionReasonError("");
      setActionError(null);
      setActionPending(false);
      setDecisionSubmitted(false);
      setOptimisticStatusLabel(null);
      setSelectedDocIndices([]);
    }
  }, [open]);

  useEffect(() => {
    setActionError(null);
    setRejectionReasonError("");
    setSelectedDocIndices([]);
  }, [detail]);

  const verificationTitle =
    detail?.verificationType ||
    (detail?.type ? `${detail.type} verification` : "Verification");
  const status =
    optimisticStatusLabel ||
    detail?.status ||
    detail?.verificationStatus ||
    collectDetailStatusCandidates(detail)[0] ||
    "—";
  const submittedAt = detail?.submittedAt;
  const user = detail?.user || {};
  const documents = useMemo(() => {
    const d = detail?.documents;
    return Array.isArray(d) ? d : [];
  }, [detail?.documents]);
  const rejectFilesPayload = useMemo(
    () =>
      selectedDocIndices
        .slice()
        .sort((a, b) => a - b)
        .map((i) => {
          const doc = documents[i];
          if (!doc) return "";
          return String(doc.fileName || doc.name || doc.title || "Document").trim();
        })
        .filter(Boolean),
    [documents, selectedDocIndices]
  );
  const specific =
    detail?.specific && typeof detail.specific === "object" ? detail.specific : null;
  const specificEntries = specific
    ? Object.entries(specific)
      .filter(([, v]) => v != null && v !== "")
      .filter(([k]) => shouldShowInAdditionalDetails(k))
    : [];

  const userInitials = String(user.name || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const decisionProbe = detail
    ? buildVerificationDecisionBody(
      detail,
      "reject",
      rejectionReason.trim(),
      detailQueryId,
      listUserId,
      assetOwnersTab,
      rejectFilesPayload
    )
    : null;
  const canSubmit =
    Boolean(detail && !loading && !fetchError && decisionProbe?.endpoint && decisionProbe.valid);
  const mutationBusy = regaBusy || nafathBusy || taufeeqBusy || varaBusy || actionPending;
  const isTerminalFromDetail = detail ? detailIndicatesTerminal(detail) : false;
  const resolvedVerificationType =
    String(verificationType || "")
      .trim()
      .toUpperCase() || (detail ? resolveDetailVerificationKind(detail) : "");
  const hints = externalStatusHints && typeof externalStatusHints === "object" ? externalStatusHints : null;
  const externalProfileLock =
    (resolvedVerificationType === "NAFATH" && isVerificationStatusTerminal(hints?.NAFATH)) ||
    (resolvedVerificationType === "VARA" && isVerificationStatusTerminal(hints?.VARA)) ||
    (resolvedVerificationType === "REGA" && isVerificationStatusTerminal(hints?.REGA)) ||
    (resolvedVerificationType === "TAUFEEQ" && isVerificationStatusTerminal(hints?.TAUFEEQ));
  const actionsLocked = isTerminalFromDetail || decisionSubmitted || externalProfileLock;

  const runDecision = async (decision) => {
    if (decision === "reject" && !String(rejectionReason ?? "").trim()) {
      setRejectionReasonError("Rejection reason is required.");
      return;
    }
    setRejectionReasonError("");
    const ctx = buildVerificationDecisionBody(
      detail,
      decision,
      rejectionReason.trim(),
      detailQueryId,
      listUserId,
      assetOwnersTab,
      decision === "reject" ? rejectFilesPayload : []
    );
    if (!ctx?.valid || !ctx.endpoint) {
      setActionError("Missing data for this verification type. Cannot submit.");
      return;
    }
    setActionPending(true);
    setActionError(null);
    try {
      const body = ctx.body;
      if (ctx.endpoint === "rega") await verifyRega(body).unwrap();
      else if (ctx.endpoint === "nafath") await verifyNafath(body).unwrap();
      else if (ctx.endpoint === "taufeeq") await verifyTaufeeq(body).unwrap();
      else if (ctx.endpoint === "vara") await verifyVara(body).unwrap();
      setDecisionSubmitted(true);
      setOptimisticStatusLabel(decision === "approve" ? "Approved" : "Rejected");
      onActionSuccess?.();
      onClose?.();
    } catch (err) {
      setActionError(err?.data?.message || err?.error || "Request failed");
    } finally {
      setActionPending(false);
    }
  };

  const handleDialogClose = (_, reason) => {
    if (mutationBusy && reason === "backdropClick") return;
    if (mutationBusy) return;
    onClose();
  };

  const toggleDocSelect = (idx) => {
    if (actionsLocked) return;
    setSelectedDocIndices((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          pr: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Verification Review
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Review the verification request and approve or reject the submission
          </Typography>
        </Box>
        <IconButton
          aria-label="Close"
          onClick={() => !mutationBusy && onClose()}
          size="small"
          disabled={mutationBusy}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "background.subtle" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: (theme) => theme.palette.accent.teal.action }} />
          </Box>
        ) : fetchError ? (
          <Alert severity="error">
            {fetchError?.data?.message || fetchError?.error || "Failed to load verification detail."}
          </Alert>
        ) : (
          <Stack spacing={2.5}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: (theme) => theme.palette.chips.infoDeep.bg,
                border: (theme) => `1px solid ${theme.palette.chips.infoDeep.bg}`,
              }}
            >
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <VerifiedUser sx={{ color: (theme) => theme.palette.chips.infoDeep.text, fontSize: 40 }} />
                  <Box>
                    <Typography fontWeight={700} fontSize={15}>
                      {verificationTitle}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5 }}>
                <CalendarToday sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  Submitted {formatSubmittedAt(submittedAt)}
                </Typography>
              </Stack>
                  </Box>
                </Stack>
                <Chip
                  label={status}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: "background.paper",
                    color: (theme) => theme.palette.chips.infoDeep.text,
                    border: (theme) => `1px solid ${theme.palette.chips.infoDeep.text}`,
                  }}
                />
              </Stack>
              {/* <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 2 }}>
                <CalendarToday sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  Submitted {formatSubmittedAt(submittedAt)}
                </Typography>
              </Stack> */}
            </Paper>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <PersonOutline fontSize="small" color="action" />
                <Typography fontWeight={700}>User Information</Typography>
              </Stack>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper" }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: "border.medium",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      color: "text.neutral",
                      flexShrink: 0,
                    }}
                  >
                    {userInitials}
                  </Box>
                  <Box flex={1}>
                    <Typography fontWeight={700}>{user.name || "—"}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.userIdLabel || "—"}
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack spacing={1}>
                      {user.nationalIdMasked != null && user.nationalIdMasked !== "" ? (
                        <Typography variant="body2">
                          <Box component="span" color="text.secondary" sx={{ mr: 1 }}>
                            National ID
                          </Box>
                          <Box component="span" fontWeight={600}>
                            {user.nationalIdMasked}
                          </Box>
                        </Typography>
                      ) : null}
                      {user.email ? (
                        <Typography variant="body2">
                          <Box component="span" color="text.secondary" sx={{ mr: 1 }}>
                            Email
                          </Box>
                          <Box component="span" fontWeight={500}>
                            {user.email}
                          </Box>
                        </Typography>
                      ) : null}
                      {user.phoneNumber != null && user.phoneNumber !== "" ? (
                        <Typography variant="body2">
                          <Box component="span" color="text.secondary" sx={{ mr: 1 }}>
                            Phone
                          </Box>
                          <Box component="span" fontWeight={500}>
                            {user.phoneNumber}
                          </Box>
                        </Typography>
                      ) : null}
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <DescriptionOutlined fontSize="small" color="action" />
                <Typography fontWeight={700}>Submitted Documents</Typography>
              </Stack>
              <Stack spacing={1.5}>
                {documents.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography color="text.secondary">No documents listed.</Typography>
                  </Paper>
                ) : (
                  documents.map((doc, idx) => (
                    <Paper
                      key={doc.url || doc.title || idx}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                        bgcolor: "background.paper",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Checkbox
                          size="small"
                          checked={selectedDocIndices.includes(idx)}
                          onChange={() => toggleDocSelect(idx)}
                          disabled={mutationBusy || actionsLocked}
                          inputProps={{
                            "aria-label": `Select document ${doc.title || doc.fileName || doc.name || "Document"} for rejection`,
                          }}
                        />
                        <InsertDriveFileOutlined color="action" />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={600} noWrap title={doc.title}>
                            {doc.title || "Document"}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility />}
                          sx={{ textTransform: "none" }}
                          disabled={!doc.url}
                          onClick={() => {
                            if (doc.url) window.open(doc.url, "_blank", "noopener,noreferrer");
                          }}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Download />}
                          sx={{ textTransform: "none" }}
                          disabled={!doc.url}
                          href={doc.url || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          component="a"
                        >
                          Download
                        </Button>
                      </Stack>
                    </Paper>
                  ))
                )}
              </Stack>
            </Box>

            {specificEntries.length > 0 ? (
              <Box>
                <Typography fontWeight={700} sx={{ mb: 1.5 }}>
                  Additional details
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper" }}>
                  <Stack spacing={1}>
                    {specificEntries.map(([k, v]) => (
                      <Typography key={k} variant="body2">
                        <Box component="span" color="text.secondary" sx={{ mr: 1, textTransform: "capitalize" }}>
                          {k.replace(/([A-Z])/g, " $1").trim()}
                        </Box>
                        <Box component="span" fontWeight={500}>
                          {k === "verifiedByNafath" ? (v ? "Approved" : "Not Approved") : formatDetailValue(v)}
                        </Box>
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              </Box>
            ) : null}

            <TextField
              label="Rejection reason"
              placeholder="Explain why this verification is being rejected (required to reject)"
              multiline
              minRows={2}
              fullWidth
              size="small"
              required
              value={rejectionReason}
              onChange={(e) => {
                setRejectionReason(e.target.value);
                setRejectionReasonError("");
              }}
              error={Boolean(rejectionReasonError)}
              helperText={rejectionReasonError || undefined}
              disabled={mutationBusy || actionsLocked}
            />
            {actionError ? (
              <Alert severity="error" onClose={() => setActionError(null)}>
                {actionError}
              </Alert>
            ) : null}
            {decisionSubmitted ? (
              <Alert severity="success">
                Verification updated. You can close this dialog when you are done reviewing.
              </Alert>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: "background.paper" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: "none" }}
          disabled={mutationBusy}
        >
          {decisionSubmitted ? "Close" : "Cancel"}
        </Button>
        <Button
          color="error"
          variant="contained"
          sx={{ textTransform: "none" }}
          disabled={
            !canSubmit ||
            mutationBusy ||
            actionsLocked ||
            !String(rejectionReason ?? "").trim()
          }
          onClick={() => runDecision("reject")}
        >
          Reject
        </Button>
        <Button
          color="success"
          variant="contained"
          startIcon={<CheckCircle />}
          sx={{ textTransform: "none" }}
          disabled={!canSubmit || mutationBusy || actionsLocked}
          onClick={() => runDecision("approve")}
        >
          Approve Verification
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdminVerifications() {
  const [mainTab, setMainTab] = useState(VIEW.INVESTOR);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(ALL_TYPES);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [mainTab, debouncedSearch, typeFilter, statusFilter, limit]);

  const queryRole = mainTab === VIEW.TAUFEEQ ? "assetOwner" : mainTab;
  const queryTab = mainTab === VIEW.TAUFEEQ ? "Taufeeq" : undefined;

  const queryArg = useMemo(
    () => ({
      role: queryRole,
      tab: queryTab,
      search: debouncedSearch,
      type: typeFilter,
      status: statusFilter,
      page,
      limit,
    }),
    [queryRole, queryTab, debouncedSearch, typeFilter, statusFilter, page, limit]
  );

  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetUsersForVerificationQuery(queryArg);

  const list = useMemo(() => getVerificationList(data), [data]);
  const pagination = useMemo(() => getVerificationPagination(data), [data]);
  const rows = useMemo(
    () => list.map((r) => mapVerificationRow(r, mainTab)),
    [list, mainTab]
  );

  const showLoader = isLoading || (isFetching && !list.length);

  const showLocationCol = mainTab === VIEW.ASSET_OWNER || mainTab === VIEW.TAUFEEQ;

  const [reviewTarget, setReviewTarget] = useState(null);

  const {
    data: detailResponse,
    isLoading: detailLoading,
    isFetching: detailFetching,
    isError: detailIsError,
    error: detailErr,
  } = useGetVerificationDetailQuery(
    { type: reviewTarget?.type ?? "", id: reviewTarget?.id ?? "" },
    { skip: !reviewTarget?.id || !reviewTarget?.type }
  );

  const detailPayload = detailResponse?.data;
  const detailBusy = Boolean(reviewTarget) && (detailLoading || detailFetching);

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ color: "text.primary", letterSpacing: -0.5 }}
        >
          Verification Center
        </Typography>
        <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
          Review and manage user verification requests
        </Typography>
      </Box>

      {isError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => refetch()}>
          {error?.data?.message || error?.error || "Failed to load verifications."}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 1.5,
          overflow: "hidden",
          border: (theme) => `1px solid ${theme.palette.border.light}`,
          boxShadow: (theme) => theme.palette.shadow.statCard,
        }}
      >
        {/* Tabs */}
        <Stack direction="row" flexWrap="wrap" sx={{ px: 1, py: 2 }}>
          {[
            {
              value: VIEW.INVESTOR,
              icon: <PersonOutline sx={{ fontSize: 18 }} />,
              title: "Investors",
              subtitle: "NAFATH Only",
            },
            {
              value: VIEW.ASSET_OWNER,
              icon: <BusinessOutlined sx={{ fontSize: 18 }} />,
              title: "Asset Owners",
              subtitle: "REGA, VARA, Property Cert",
            },
            {
              value: VIEW.TAUFEEQ,
              icon: <DescriptionOutlined sx={{ fontSize: 18 }} />,
              title: "Taufeeq",
              subtitle: "Property valuation",
            },
          ].map((t) => {
            const active = mainTab === t.value;
            return (
              <Button
                key={t.value}
                onClick={() => setMainTab(t.value)}
                startIcon={t.icon}
                disableRipple
                sx={{
                  textTransform: "none",
                  px: 2.5,
                  py: 1.25,
                  borderRadius: 1.5,
                  mr: 1,
                  minWidth: 0,
                  bgcolor: (theme) =>
                    active ? theme.palette.accent.info.border : "transparent",
                  color: "text.primary",
                  "&:hover": {
                    bgcolor: (theme) =>
                      active
                        ? theme.palette.accent.info.border
                        : theme.palette.background.subtle,
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography
                    fontWeight={700}
                    fontSize={14}
                    sx={{ color: "text.primary" }}
                  >
                    {t.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.muted", fontSize: 12 }}
                  >
                    {t.subtitle}
                  </Typography>
                </Stack>
              </Button>
            );
          })}
        </Stack>

        <Divider />

        {/* Filters row */}
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          gap={1.5}
          sx={{ px: 3, py: 2 }}
        >
          <TextField
            size="small"
            placeholder="Search by name, ID, deed, or location..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{
              flex: "1 1 280px",
              minWidth: 260,
              maxWidth: 480,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                bgcolor: "background.paper",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: "text.muted" }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              sx={{ borderRadius: 1 }}
            >
              <MenuItem value={ALL_TYPES}>All Types</MenuItem>
              <MenuItem value="NAFATH">NAFATH</MenuItem>
              <MenuItem value="REGA">REGA</MenuItem>
              <MenuItem value="VARA">VARA</MenuItem>
              <MenuItem value="Taufeeq">Taufeeq</MenuItem>
            </Select>
          </FormControl>
          <IconButton
            size="small"
            sx={{
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              borderRadius: 1.25,
              color: "text.muted",
            }}
            aria-label="Filters"
          >
            <FilterList fontSize="small" />
          </IconButton>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ borderRadius: 1 }}
            >
              <MenuItem value={ALL_STATUS}>All Status</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="In Review">In Review</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: "border.light",
              color: "text.neutral",
              borderRadius: 1,
              px: 2,
            }}
          >
            Refresh
          </Button>
        </Stack>

        <Divider />
        {showLoader ? (
          <AdminVerificationsSkeleton showLocationCol={showLocationCol} />
        ) : (
          <>
            <TableContainer sx={{ minHeight: 480 }}>
              <Table sx={{ minWidth: 960 }}>
                <TableHead sx={{ bgcolor: (theme) => theme.palette.table.headBg }}>
                  <TableRow>
                    {["ID", "Type", "User / Company", ...(showLocationCol ? ["Location"] : []), "Status", "Actions"].map(
                      (h) => (
                        <TableCell
                          key={h}
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: (theme) => theme.palette.table.headText,
                            py: 2,
                          }}
                        >
                          {h}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={showLocationCol ? 7 : 6}
                        align="center"
                        sx={{ py: 6 }}
                      >
                        <Typography sx={{ color: "text.muted" }}>No verification requests found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow
                        key={r.key}
                        hover
                        sx={{
                          "&:hover": {
                            bgcolor: (theme) => theme.palette.table.rowHover,
                          },
                          "& td": {
                            borderColor: (theme) => theme.palette.border.subtle,
                            verticalAlign: "top",
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            fontSize={13}
                            fontWeight={700}
                            sx={{ color: "text.primary" }}
                          >
                            {r.displayId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TypeBadge label={r.typeLabel} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                bgcolor: "border.medium",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 14,
                                fontWeight: 700,
                                color: "text.neutral",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                            >
                              {r.avatar ? (
                                <Box component="img" src={r.avatar} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                String(r.name || "?")
                                  .split(/\s+/)
                                  .map((w) => w[0])
                                  .join("")
                                  .slice(0, 2)
                                  .toUpperCase()
                              )}
                            </Box>
                            <Box>
                              <Typography fontSize={14} fontWeight={600} sx={{ color: "text.primary" }}>
                                {r.name}
                              </Typography>
                              {r.userSubId ? (
                                <Typography variant="caption" sx={{ color: "text.muted" }}>
                                  {r.userSubId}
                                </Typography>
                              ) : null}
                            </Box>
                          </Stack>
                        </TableCell>
                        {showLocationCol ? (
                          <TableCell sx={{ maxWidth: 200 }}>
                            <Stack direction="row" spacing={0.5} alignItems="flex-start">
                              <LocationOn sx={{ fontSize: 18, color: "text.faint", mt: 0.25 }} />
                              <Typography fontSize={13} sx={{ color: "text.primary" }}>{r.location}</Typography>
                            </Stack>
                          </TableCell>
                        ) : null}
                        <TableCell>
                          <StatusCell statusNorm={r.statusNorm} statusRaw={r.statusRaw} />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: 1.25,
                              borderColor: "border.light",
                              color: "text.primary",
                            }}
                            disabled={!r.detailId}
                            onClick={() => {
                              const type = verificationTypeQueryParam(r.typeLabel);
                              if (!r.detailId || !type) return;
                              setReviewTarget({
                                id: r.detailId,
                                type,
                                listUserId: r.listUserId,
                              });
                            }}
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={Math.max(pagination.total, rows.length)}
              page={Math.max(0, page - 1)}
              onPageChange={(_, p) => setPage(p + 1)}
              rowsPerPage={limit}
              onRowsPerPageChange={(e) => {
                setLimit(parseInt(e.target.value, 10));
                setPage(1);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Rows per page"
            />
          </>
        )}
      </Paper>

      <VerificationReviewDialog
        open={Boolean(reviewTarget)}
        onClose={() => setReviewTarget(null)}
        loading={detailBusy}
        fetchError={detailIsError ? detailErr : null}
        detail={detailIsError ? null : detailPayload}
        detailQueryId={reviewTarget?.id}
        listUserId={reviewTarget?.listUserId}
        assetOwnersTab={mainTab === VIEW.ASSET_OWNER}
        verificationType={reviewTarget?.type}
        onActionSuccess={() => refetch()}
      />
    </Box>
  );
}
